import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import axios from "axios";
import { Footer } from "../components/Footer";
import { LanguageToggle } from "../components/LanguageToggle";
import { HISTORY_UI, HISTORY_ISLAND_LABELS } from "../lib/historyMeta";
import { useSeoTags } from "../lib/useSeoTags";

const SITE = "https://islandsdaily.com";
const API  = process.env.REACT_APP_BACKEND_URL;

const readStoredLang = () => {
  try {
    const s = localStorage.getItem("ir_lang");
    if (["en","es","nl","pap","pap_aw"].includes(s)) return s;
  } catch (_e) {}
  return "en";
};

const pickDescription = (photo, lang) => {
  const d = photo.descriptions || {};
  if (d[lang]) return { text: d[lang], is_auto: false };
  if (d.en)    return { text: d.en, is_auto: lang !== "en" };
  const first = Object.keys(d)[0];
  if (first)   return { text: d[first], is_auto: lang !== first };
  return { text: HISTORY_UI.no_description[lang] || HISTORY_UI.no_description.en, is_auto: false };
};

// Build a full ImageObject JSON-LD block per Google's rich-results
// requirements (contentUrl, creator, creditText, license, acquireLicensePage).
const buildImageSchema = (p) => ({
  "@context": "https://schema.org",
  "@type": "ImageObject",
  contentUrl: p.image_url,
  thumbnailUrl: p.thumb_url,
  name: p.commons_title,
  caption: (p.descriptions?.en || p.commons_title || "").slice(0, 300),
  width: p.width,
  height: p.height,
  dateCreated: p.date_display || (p.year ? String(p.year) : undefined),
  creator: p.photographer ? { "@type": "Person", name: p.photographer } : undefined,
  creditText: p.credit_text || undefined,
  copyrightNotice: p.credit_text || undefined,
  license: p.license_deed_url || undefined,
  acquireLicensePage: p.commons_url_page || undefined,
  url: `${SITE}/history/${p.slug}`,
});

export default function HistoryPhotoPage() {
  const { slug } = useParams();
  const [lang, setLang] = useState(readStoredLang);
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    const handler = () => setLang(readStoredLang());
    window.addEventListener("ir_lang_changed", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("ir_lang_changed", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);
  const setLangPersist = useCallback((next) => {
    try { localStorage.setItem("ir_lang", next); window.dispatchEvent(new Event("ir_lang_changed")); } catch(_e){}
    setLang(next);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(false);
    axios.get(`${API}/api/history/${slug}`).then((r) => {
      if (cancelled) return;
      setPhoto(r.data); setLoading(false);
    }).catch(() => { if (!cancelled) { setErr(true); setLoading(false); }});
    return () => { cancelled = true; };
  }, [slug]);

  const seoConfigs = useMemo(() => {
    if (!photo) return {};
    const desc = (photo.descriptions?.[lang] || photo.descriptions?.en || photo.commons_title || "").slice(0, 250);
    const canonical = `${SITE}/history/${photo.slug}`;
    const cfg = {
      title: `${photo.commons_title?.replace(/^File:/i, "").replace(/\.(jpe?g|png|tiff?)$/i,"")} — IslandsDaily History`,
      description: desc,
      canonical,
      alternates: ["en","nl","es","pap"].map((h) => ({hreflang:h,href:canonical}))
                     .concat([{hreflang:"x-default",href:canonical}]),
    };
    return { en: cfg, nl: cfg, es: cfg, pap: cfg, pap_aw: cfg };
  }, [photo, lang]);
  useSeoTags(lang, seoConfigs);

  if (loading) return <div className="p-8" data-testid="history-photo-loading">Loading…</div>;
  if (err || !photo) return (
    <div className="min-h-screen bg-white" data-testid="history-photo-404">
      <div className="max-w-3xl mx-auto p-8">
        <Link to="/history" className="text-[#0AADA0]">← {HISTORY_UI.back_all_photos[lang]}</Link>
        <p className="mt-4 text-[#3D4C52]">{HISTORY_UI.no_description[lang]}</p>
      </div>
    </div>
  );

  const desc = pickDescription(photo, lang);
  const islandLabel = HISTORY_ISLAND_LABELS[photo.island] || photo.island;

  return (
    <div className="min-h-screen bg-white" data-testid={`history-photo-${photo.slug}`}>
      <script
        type="application/ld+json"
        data-testid="history-image-schema"
        dangerouslySetInnerHTML={{__html: JSON.stringify(buildImageSchema(photo))}}
      />
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#EEEEEE]">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link
            to={photo.island ? `/${photo.island}/history` : "/history"}
            className="inline-flex items-center gap-1.5 text-[13px] text-[#6B7C82] hover:text-[#1A2E35]"
            data-testid="history-photo-back"
          >
            <ArrowLeft size={15} /> {islandLabel}
          </Link>
          <Link to="/" className="ml-auto font-serif-display text-[18px] leading-none">
            <span className="text-[#1A1A1A]">Islands</span>
            <span className="text-[#0AADA0]">Daily</span>
          </Link>
          <LanguageToggle lang={lang} onChange={setLangPersist} />
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="bg-[#F5F5F5] rounded-lg overflow-hidden mb-6">
          <img
            src={photo.image_url}
            alt={photo.commons_title || ""}
            className="w-full h-auto max-h-[70vh] object-contain mx-auto"
            data-testid="history-photo-hero"
          />
        </div>

        <p className="text-[15px] text-[#1A2E35] leading-[1.7] mb-2" data-testid="history-photo-description">
          {desc.text}
        </p>
        {desc.is_auto && (
          <p className="text-[10px] italic text-[#6B7C82] mb-4" data-testid="history-photo-auto-tag">
            {HISTORY_UI.translated_auto[lang]}
          </p>
        )}

        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mt-6 text-[13px]" data-testid="history-photo-meta">
          {photo.date_display && (
            <div>
              <dt className="text-[10px] uppercase tracking-wider text-[#6B7C82]">{HISTORY_UI.meta_date[lang]}</dt>
              <dd className="text-[#1A2E35]">{photo.date_display}</dd>
            </div>
          )}
          {photo.photographer && (
            <div>
              <dt className="text-[10px] uppercase tracking-wider text-[#6B7C82]">{HISTORY_UI.meta_photographer[lang]}</dt>
              <dd className="text-[#1A2E35]">{photo.photographer}</dd>
            </div>
          )}
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-[#6B7C82]">{HISTORY_UI.meta_license[lang]}</dt>
            <dd>
              {photo.license_deed_url ? (
                <a href={photo.license_deed_url} target="_blank" rel="noopener noreferrer" className="text-[#0AADA0] hover:underline">
                  {photo.license_short}
                </a>
              ) : photo.license_short}
            </dd>
          </div>
          {photo.width && photo.height && (
            <div>
              <dt className="text-[10px] uppercase tracking-wider text-[#6B7C82]">{HISTORY_UI.meta_dimensions[lang]}</dt>
              <dd className="text-[#1A2E35]">{photo.width} × {photo.height} px</dd>
            </div>
          )}
        </dl>

        {photo.credit_text && (
          <p className="mt-6 text-[11px] text-[#6B7C82] leading-[1.5] border-t border-[#EEE] pt-4" data-testid="history-photo-credit">
            {photo.credit_text}
          </p>
        )}

        <a
          href={photo.commons_url_page}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block mt-4 text-[13px] text-[#0AADA0] hover:underline"
          data-testid="history-photo-commons-link"
        >
          {HISTORY_UI.view_on_commons[lang]} →
        </a>
      </main>
      <Footer />
    </div>
  );
}
